import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<DecisionRationaleModal />` — clinical-rationale disclosure modal
 * per CPL Phase 5 Phase D IMPL composition guide pair-with-DEV5 §3.1
 * + §7 i18n bundle (2026-05-14).
 *
 * Opens from `<PaTrackerContainer />` when status reaches a terminal
 * state (`approved` | `denied`) and the patient clicks the "View
 * clinical rationale" CTA. Renders payer-provided clinical rationale
 * with explicit substrate-honesty framing — the substrate does not
 * make clinical decisions; this UI surfaces what the payer reasoned.
 *
 * # Phase 5 §G.c extension — post-PA-decision audit trail cross-link
 *
 * Optional `onViewAuditTrail?: () => void` callback prop opts the modal
 * into the audit trail cross-link UX per Phase 5 §G design memo v0
 * (PR #953) §4 N+5 phasing + §3 OQ-G-4 lean (a) single-page UX. When
 * provided, modal renders a "View audit trail" CTA after the substrate-
 * honesty disclosure + narrative; clicking the CTA invokes the callback
 * (typically closes the modal + scrolls `<PriorAuthView />` to the
 * `<PostDecisionAuditTrailContainer />` section per single-page UX
 * preservation discipline).
 *
 * Caller-supplied `viewAuditTrailLabel?: string` defers i18n migration
 * scope per Phase 5 §G memo §3 OQ-G-3 lean (b) — modal renders the
 * caller's label OR hardcoded fallback; i18n bundle migration absorption
 * substrate-state-event-paced at cohort OQ-G-3 disposition convergence
 * canonical preservation forward.
 *
 * # Doctrinal anchors preserved
 *
 *   - Substrate-as-router not substrate-as-decision-maker — the
 *     substrate-honesty paragraph is REQUIRED rendering; not a
 *     dismissible footnote.
 *   - Recovery UX honesty analog — explicit "the payer decided" not
 *     "the substrate decided" framing canonical preservation forward.
 *   - Pattern B no-parallel-rendering-surface — modal is client-side
 *     React; payer rationale text composes via i18n bundle or
 *     orchestrator-supplied narrative.
 *
 * # Sandbox-grade scope-fence
 *
 * v0 renders the substrate-honesty disclosure + optional
 * orchestrator-supplied narrative + an appeal CTA + an optional
 * audit-trail cross-link CTA (Phase 5 §G.c). Production-tier
 * cycles add: payer policy reference link, clinical guideline
 * anchor (ACR AC / ACP / AUA / USPSTF per CIL UC22 v0.2 §6),
 * patient-comprehensible rationale paragraph rendered from the
 * payer's structured decision record.
 */
import * as React from 'react';
import { useLocale } from '../../i18n/locale-context.js';
export function DecisionRationaleModal(props) {
    const { t } = useLocale();
    // Close on Escape per a11y dialog conventions.
    React.useEffect(() => {
        const handler = (ev) => {
            if (ev.key === 'Escape')
                props.onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [props]);
    return (_jsx("div", { className: "prior-auth-rationale__backdrop", "data-testid": "prior-auth-rationale-modal", role: "presentation", onClick: props.onClose, children: _jsxs("div", { className: "prior-auth-rationale__dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "prior-auth-rationale-heading", onClick: (ev) => ev.stopPropagation(), children: [_jsxs("header", { className: "prior-auth-rationale__header", children: [_jsx("h2", { id: "prior-auth-rationale-heading", children: t('account.prior_auth.rationale.heading') }), _jsx("button", { type: "button", className: "prior-auth-rationale__close-cta", "aria-label": t('account.prior_auth.rationale.close_aria'), onClick: props.onClose, "data-testid": "prior-auth-rationale-close-cta", children: "\u00D7" })] }), _jsx("p", { className: "prior-auth-rationale__substrate-honesty", children: t('account.prior_auth.rationale.substrate_honesty') }), props.narrative !== undefined ? (_jsx("p", { className: "prior-auth-rationale__narrative", "data-testid": "prior-auth-rationale-narrative", children: props.narrative })) : null, props.onViewAuditTrail !== undefined ? (_jsx("button", { type: "button", className: "prior-auth-rationale__view-audit-trail-cta", onClick: props.onViewAuditTrail, "data-testid": "prior-auth-rationale-view-audit-trail-cta", children: props.viewAuditTrailLabel ?? 'View audit trail' })) : null, props.onRequestAppeal !== undefined ? (_jsx("button", { type: "button", className: "prior-auth-rationale__appeal-cta", onClick: props.onRequestAppeal, "data-testid": "prior-auth-rationale-appeal-cta", children: t('account.prior_auth.rationale.appeal_cta') })) : null] }) }));
}
//# sourceMappingURL=decision-rationale-modal.js.map